// src/redux/authentication/utils/core/attachAPIClientAuthInterceptor.js
import apiClient, { formEncodedApiClient } from '../axios';
import authService from '../authService';
import store from '../../../../store';
import { logoutUser, clearAuth } from '../authslice';

const NO_REFRESH = ['/token', '/users/register', '/logout', '/refresh', '/users/me']; // ⬅️ added /users/me

const isPublicOrNoRefresh = (url = '') =>
  NO_REFRESH.some(seg => url.includes(seg));

let isRefreshing = false;
let waiters = [];

function notifyWaiters() {
  waiters.forEach(({ resolve }) => resolve());
  waiters = [];
}

function queueUntilRefreshed() {
  return new Promise(resolve => waiters.push({ resolve }));
}

function forceLogout() {
  try {
    store.dispatch(logoutUser());
  } catch {}
  store.dispatch(clearAuth());

  const currentPath = window.location.pathname;
  if (currentPath.startsWith("/login")) {
    // Already on login → just reload it clean
    window.location.assign("/login?session=expired");
    return;
  }

  const ret = encodeURIComponent(currentPath + window.location.search);
  window.location.assign(`/login?session=expired&returnTo=${ret}`);
}

function attach(instance) {
  instance.interceptors.response.use(
    res => res,
    async error => {
      const resp = error?.response;
      const cfg = error?.config;

      if (!resp) {
        console.warn('[Axios] Network error (no response):', error?.message);
        return Promise.reject(error);
      }

      if (cfg?._authRetried) return Promise.reject(error);

      // Unauthorized → attempt silent refresh unless exempt
      if (resp.status === 401 && !isPublicOrNoRefresh(cfg?.url)) {
        // 🛑 If no refresh token cookie exists → logout immediately
        if (!document.cookie.includes("refresh_token")) {
          forceLogout();
          return Promise.reject(error);
        }

        try {
          if (isRefreshing) {
            await queueUntilRefreshed();
          } else {
            isRefreshing = true;
            try {
              await authService.refreshToken();
            } finally {
              isRefreshing = false;
              notifyWaiters();
            }
          }
          cfg._authRetried = true;
          return instance.request(cfg);
        } catch (e) {
          console.warn('[Axios] Refresh failed, forcing logout');
          forceLogout();
          return Promise.reject(e);
        }
      }

      return Promise.reject(error);
    }
  );
}

export default function attachAxiosAuthInterceptors() {
  attach(apiClient);
  attach(formEncodedApiClient);
}
